import { useState, useMemo, useEffect, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { agruparProduccion, exportProduccionDiariaExcel, type GrupoProduccion, type LoteDetalle } from '@/utils/exportProduccionDiariaExcel'
import { produccionDiariaApi } from '@/services/api'
import type { AggColumn, MarcasFilas, ProduccionDiariaRow, ConsumoOpRow, ConsumoProduccionRow, ProduccionDiariaSortKey } from '@/types'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import { useColumnAggregations } from '@/hooks/useColumnAggregations'
import { adherenceBarCls, adherenceTextCls } from '@/utils/adherencia'
// Mismo componente que la columna «Motivo desv.» de Secuencia: la nota es
// multilínea, así que la celda recorta a una línea y el texto completo se abre
// en tarjeta al pasar el ratón (o al enfocar con el teclado).
import { CommentHoverCard } from '@/components/production-sequence/CommentHoverCard'
import { Loader }         from '@/components/common/Loader'
import { StatusBadge }    from '@/components/common/StatusBadge'
import { EmptyState }     from '@/components/common/EmptyState'
import { Pagination }     from '@/components/common/Pagination'
import { SortableHeader } from '@/components/common/SortableHeader'
import { TableToolbar }   from '@/components/common/TableToolbar'
import { AggregationMenu }    from '@/components/common/AggregationMenu'
import { TableTotalsFooter }  from '@/components/common/TableTotalsFooter'
import { AdjuntosModal } from '@/components/pedidosCompra/AdjuntosModal'
import { AvisoExport, errorExportDe, type ErrorExport } from '@/components/common/AvisoExport'
import { BarraMarcas, CabeceraMarca, FilaMarcable, MarcaCelda, useMarcasFilas } from '@/components/common/MarcasFilas'
import { claveFila } from '@/utils/marcasFilas'

const PAGE_SIZE = 50

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  dark: boolean
  sorted: ProduccionDiariaRow[]
  sortKey: ProduccionDiariaSortKey
  sortDir: 'asc' | 'desc'
  isLoading: boolean
  isEmpresaAll: boolean
  setSortKey: (k: ProduccionDiariaSortKey) => void
  setSortDir: (d: 'asc' | 'desc') => void
  desde: string
  hasta: string
  empresa: string
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtQty(n: number) { return n.toLocaleString('es-ES', { maximumFractionDigits: 2 }) }

function fmtDate(iso?: string) {
  if (!iso) return '—'
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}

function rendPct(producida: number, planificada: number) {
  if (!planificada) return null
  return Math.round((producida / planificada) * 100)
}

// ── Columnas ──────────────────────────────────────────────────────────────────
// Fuente única para la cabecera, el pie de totales y el nº de columnas del
// colSpan de las subtablas. El cuerpo se sigue pintando a mano porque cada
// celda tiene su propio formato (badges, barra de rendimiento, botones).

interface PdCol {
  key: string
  /** Sin etiqueta → columna sin cabecera visible (la del desplegable). */
  labelKey?: string
  sortKey?: ProduccionDiariaSortKey
  align?: 'right'
  numeric?: boolean
  onlyEmpresaAll?: boolean
}

const COLS: PdCol[] = [
  { key: 'marca' },
  { key: 'expand' },
  { key: 'docs',           labelKey: 'production.btn.docs' },
  { key: 'numOp',          labelKey: 'production.col.opNum',       sortKey: 'numOp' },
  { key: 'articulo',       labelKey: 'production.col.article',     sortKey: 'articulo' },
  { key: 'descripcion',    labelKey: 'production.col.description' },
  { key: 'lotes',          labelKey: 'production.col.lots' },
  { key: 'estadoOp',       labelKey: 'production.col.status',      sortKey: 'estadoOp' as ProduccionDiariaSortKey },
  { key: 'almacenDestino', labelKey: 'production.col.warehouse',   sortKey: 'almacenDestino' as ProduccionDiariaSortKey, onlyEmpresaAll: true },
  { key: 'fecha',          labelKey: 'production.col.lastDate',    sortKey: 'fecha' },
  // QTY OT antes de QTY producida: se lee «lo pedido → lo hecho → el %».
  { key: 'qtyPlanificada', labelKey: 'production.col.qtyPlanned',  sortKey: 'qtyPlanificada', align: 'right', numeric: true },
  { key: 'qtyProducida',   labelKey: 'production.col.qtyProduced', sortKey: 'qtyProducida',   align: 'right', numeric: true },
  { key: 'rendimiento',    labelKey: 'production.col.performance', align: 'right', numeric: true },
  // Solo para el rol Empresa.All: la nota de desviación es información interna
  // de planta y no se expone a los roles de una sola empresa.
  { key: 'motivoDesv',     labelKey: 'production.col.deviationReason', onlyEmpresaAll: true },
]

// La tabla principal muestra una fila por OP, así que los totales se calculan
// sobre los grupos y no sobre las filas de lote.
const AGG_COLS: AggColumn<GrupoProduccion>[] = [
  { key: 'qtyPlanificada', numeric: true, valueKey: 'qtyPlanificada',    decimals: 2, defaultAgg: 'sum' },
  { key: 'qtyProducida',   numeric: true, valueKey: 'qtyProducidaTotal', decimals: 2, defaultAgg: 'sum' },
  // Porcentaje derivado: la suma no significaría nada, sólo se ofrece la media.
  { key: 'rendimiento',    numeric: true, decimals: 1, value: g => rendPct(g.qtyProducidaTotal, g.qtyPlanificada) },
]

const AGG_KEYS = new Set(AGG_COLS.map(c => c.key))

// ── Lotes subtable ────────────────────────────────────────────────────────────

function LotesSubtable({ dark, lotes, distinctLotCount, isEmpresaAll, colSpan, marcas, clavePadre }: {
  dark: boolean
  lotes: LoteDetalle[]       // pre-sorted: lote ASC, fecha ASC
  distinctLotCount: number
  isEmpresaAll: boolean
  colSpan: number
  // Las sublíneas comparten el almacén de marcas de la tabla padre: el recuento
  // de la franja y el «limpiar» tienen que ver también lo marcado aquí dentro.
  marcas: MarcasFilas
  /** Clave de la fila de la OP. La de cada lote empieza por ella, así que un
   *  lote marcado no puede aparecer marcado bajo otra OP. */
  clavePadre: string
}) {
  const { t } = useTranslation()
  const subBg   = dark ? 'bg-indigo-950/30' : 'bg-sky-50/40'
  const thCls   = `text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 text-left whitespace-nowrap ${dark ? 'text-sky-400' : 'text-sky-600'}`
  const tdCls   = `text-[11px] px-3 py-1.5 ${dark ? 'text-slate-300' : 'text-slate-700'}`
  const mutedCl = dark ? 'text-slate-600' : 'text-slate-300'

  return (
    <td colSpan={colSpan} className={`${subBg} p-0`}>
      <div className={`pl-10 pr-4 py-2 overflow-x-auto border-b ${dark ? 'border-white/6' : 'border-sky-100'}`}>
        <div className={`text-[10px] font-bold uppercase tracking-widest mb-1.5 ${dark ? 'text-sky-400' : 'text-sky-600'}`}>
          {t('production.lot.title')} ({distinctLotCount})
        </div>
        <table className="w-full border-collapse text-left">
          <thead>
            <tr>
              <CabeceraMarca />
              <th className={thCls}>{t('production.col.lot')}</th>
              <th className={thCls}>{t('production.col.date')}</th>
              {isEmpresaAll && <th className={thCls}>{t('production.col.warehouse')}</th>}
              <th className={`${thCls} text-right`}>{t('production.lot.qtyProduced')}</th>
              <th className={`${thCls} text-right`}>{t('production.lot.qtyTotal')}</th>
            </tr>
          </thead>
          <tbody>
            {lotes.map((l, i) => {
              // Same lot number as previous row → show continuation marker instead of repeating
              //const isContinuation = i > 0 && l.lote !== undefined && l.lote === lotes[i - 1].lote
              const isContinuation = false
              // Clave de negocio del lote: lo que lo identifica dentro de la OP
              // es (lote, fecha, almacén) — nunca `i`, que se mueve al reordenar
              // la subtabla o al cambiar el filtro de empresa.
              const claveLote = claveFila(clavePadre, 'lote', l.lote, l.fecha, l.almacenDestino)
              return (
                <FilaMarcable key={i} marcas={marcas} clave={claveLote} dark={dark}
                  className={dark ? 'border-t border-white/5' : 'border-t border-slate-100'}>
                  <MarcaCelda marcas={marcas} clave={claveLote} dark={dark} densa />
                  {/* Lot number — show only for first row of each lot */}
                  <td className={`${tdCls} font-mono font-semibold`}>
                    {isContinuation ? (
                      <span className={`${mutedCl} select-none`} title={l.lote ?? ''}>└ ─</span>
                    ) : (
                      l.lote ?? '—'
                    )}
                  </td>
                  <td className={tdCls}>{fmtDate(l.fecha)}</td>
                  {isEmpresaAll && <td className={tdCls}>{l.almacenDestino ?? '—'}</td>}
                  <td className={`${tdCls} text-right tabular-nums font-semibold ${dark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                    {fmtQty(l.qtyProducida)}
                  </td>
                  <td className={`${tdCls} text-right tabular-nums`}>{fmtQty(l.qtyTotalReportada)}</td>
                </FilaMarcable>
              )
            })}
          </tbody>
        </table>
      </div>
    </td>
  )
}

// ── Consumos subtable ─────────────────────────────────────────────────────────

function ConsumosSubRow({ dark, numOp, colSpan, marcas, clavePadre }: {
  dark: boolean
  numOp: string
  colSpan: number
  // Mismo almacén que la tabla padre (ver LotesSubtable).
  marcas: MarcasFilas
  clavePadre: string
}) {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['consumos-op', numOp],
    queryFn: () => produccionDiariaApi.getConsumosOp(numOp),
    staleTime: 10 * 60 * 1000,
  })

  const consumos: ConsumoOpRow[] = (data?.data ?? []) as ConsumoOpRow[]
  const thCls = `text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 text-left whitespace-nowrap ${dark ? 'text-slate-500' : 'text-slate-400'}`
  const tdCls = `text-[11px] px-3 py-1.5 ${dark ? 'text-slate-300' : 'text-slate-700'}`
  const subBg = dark ? 'bg-slate-800/60' : 'bg-slate-50'

  if (isLoading) {
    return (
      <td colSpan={colSpan} className={`px-4 py-3 ${subBg}`}>
        <Loader label={t('production.consumo.loading')} size={10} />
      </td>
    )
  }
  if (isError || !consumos.length) {
    return (
      <td colSpan={colSpan} className={`px-4 py-3 ${subBg}`}>
        <div className={`text-[11px] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
          {isError ? t('production.consumo.error') : t('production.consumo.none')}
        </div>
      </td>
    )
  }

  return (
    <td colSpan={colSpan} className={`${subBg} p-0`}>
      <div className="pl-10 pr-4 py-2 overflow-x-auto">
        <div className={`text-[10px] font-bold uppercase tracking-widest mb-1.5 ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
          {t('production.consumo.title')}
        </div>
        <table className="w-full border-collapse text-left">
          <thead>
            <tr>
              <CabeceraMarca />
              <th className={thCls}>{t('production.consumo.component')}</th>
              <th className={thCls}>{t('production.consumo.description')}</th>
              <th className={thCls}>{t('production.consumo.family')}</th>
              <th className={`${thCls} text-right`}>{t('production.consumo.qtyTheoretical')}</th>
              <th className={`${thCls} text-right`}>{t('production.consumo.qtyReal')}</th>
              <th className={`${thCls} text-right`}>{t('production.consumo.deviation')}</th>
              <th className={`${thCls} text-right`}>{t('production.consumo.deviationPct')}</th>
              <th className={thCls}>{t('production.consumo.um')}</th>
            </tr>
          </thead>
          <tbody>
            {consumos.map((c, i) => {
              const alerta = c.alertaDesviacion === 1
              // `planificacion_produccion_consumos` trae una fila por
              // (num_op, componente): el componente identifica la sublínea
              // dentro de su OP.
              const claveC = claveFila(clavePadre, 'consumo', c.componente)
              return (
                <FilaMarcable key={`${c.componente}-${i}`} marcas={marcas} clave={claveC} dark={dark}
                  className={dark ? 'border-t border-white/5' : 'border-t border-slate-100'}>
                  <MarcaCelda marcas={marcas} clave={claveC} dark={dark} densa />
                  <td className={`${tdCls} font-mono font-semibold`}>{c.componente}</td>
                  <td className={`${tdCls} max-w-[200px] truncate`}>{c.descripcionComponente}</td>
                  <td className={tdCls}>{c.familiaComponente ?? '—'}</td>
                  <td className={`${tdCls} text-right tabular-nums`}>{fmtQty(c.qtyTeorica)}</td>
                  <td className={`${tdCls} text-right tabular-nums`}>{fmtQty(c.qtyReal)}</td>
                  <td className={`${tdCls} text-right tabular-nums ${alerta ? 'text-red-500 font-semibold' : ''}`}>
                    {fmtQty(c.desviacion)}
                  </td>
                  <td className={`${tdCls} text-right tabular-nums`}>
                    {alerta ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-500/15 text-red-500">
                        <svg className="w-2.5 h-2.5 shrink-0" viewBox="0 0 10 10" fill="currentColor">
                          <path d="M5 1L9.33 8.5H.67L5 1z"/>
                        </svg>
                        {c.pctDesviacion.toFixed(1)}%
                      </span>
                    ) : (
                      <span className={dark ? 'text-slate-400' : 'text-slate-500'}>{c.pctDesviacion.toFixed(1)}%</span>
                    )}
                  </td>
                  <td className={tdCls}>{c.um ?? '—'}</td>
                </FilaMarcable>
              )
            })}
          </tbody>
        </table>
      </div>
    </td>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function ProduccionDiariaTable({
  dark, sorted, sortKey, sortDir, isLoading, isEmpresaAll, setSortKey, setSortDir, desde, hasta, empresa,
}: Props) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  // Marcas del usuario. La OP identifica la fila: la tabla agrupa por ella, así
  // que no se repite y la marca aguanta ordenar, filtrar y paginar.
  const marcas = useMarcasFilas('produccionDiaria')
  const [page, setPage] = useState(1)
  const [adjuntosNumOp, setAdjuntosNumOp] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const [errorExport, setErrorExport] = useState<ErrorExport | null>(null)

  const visibleCols = useMemo(
    () => COLS.filter(c => !c.onlyEmpresaAll || isEmpresaAll),
    [isEmpresaAll],
  )
  const colCount = visibleCols.length

  const cardBg    = dark ? 'bg-slate-900 border-white/8'   : 'bg-white border-slate-200'
  const mutedTxt  = dark ? 'text-slate-400'                 : 'text-slate-500'
  const btnBase   = 'text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors font-medium cursor-pointer'
  const btnNeutral = dark
    ? `${btnBase} border-white/10 bg-white/5 text-slate-300 hover:bg-white/10`
    : `${btnBase} border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm`
  const thBase = `px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider select-none whitespace-nowrap ${dark ? 'text-slate-400' : 'text-slate-500'}`
  const tdBase = `px-3 py-2.5 text-[13px] whitespace-nowrap ${dark ? 'text-slate-300' : 'text-slate-700'}`

  // Group sorted rows by numOp, preserving order of first appearance.
  // Within each group, lotes are sorted by lot number (natural) then date.
  const grupos: GrupoProduccion[] = useMemo(() => agruparProduccion(sorted), [sorted])

  // Totales sobre todos los grupos filtrados, no sobre la página visible.
  const agg = useColumnAggregations({ storageKey: 'produccionDiaria', rows: grupos, columns: AGG_COLS })

  // Reset to page 1 when groups change (new filter applied)
  useEffect(() => { setPage(1) }, [grupos.length])

  const totalGroups = grupos.length
  const totalPages  = Math.max(1, Math.ceil(totalGroups / PAGE_SIZE))
  const safePage    = Math.min(page, totalPages)
  const pagedGrupos = grupos.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  function toggleExpand(numOp: string) {
    setExpanded(prev => {
      const s = new Set(prev)
      s.has(numOp) ? s.delete(numOp) : s.add(numOp)
      return s
    })
  }

  function handleSort(k: ProduccionDiariaSortKey) {
    if (k === sortKey) setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    else { setSortKey(k); setSortDir('asc') }
    setPage(1)
  }

  // Excel export: un bloque por OP (cabecera + lotes + BOM), matriz por cabecera.
  async function exportExcel() {
    if (exporting) return
    setExporting(true)
    setErrorExport(null)
    try {
      const res = await produccionDiariaApi.getConsumosExport({ desde, hasta, empresa: empresa || undefined })
      const bomByOp = new Map<string, ConsumoProduccionRow[]>()
      for (const c of (res.data ?? [])) {
        const arr = bomByOp.get(c.numOp) ?? []
        arr.push(c)
        bomByOp.set(c.numOp, arr)
      }
      await exportProduccionDiariaExcel(
        grupos, bomByOp, t,
        `${t('production.excel.filename')}_${new Date().toISOString().slice(0, 10)}`,
        isEmpresaAll,
        fmtArt,
      )
    } catch (e) {
      setErrorExport(errorExportDe(e))
    } finally {
      setExporting(false)
    }
  }

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className={`rounded-xl border overflow-hidden shadow-sm ${cardBg}`}>
        <div className={`flex items-center gap-2 px-4 py-2.5 border-b ${dark ? 'border-white/6' : 'border-slate-100'}`}>
          <div className={`h-4 w-24 rounded animate-pulse ${dark ? 'bg-white/6' : 'bg-slate-100'}`} />
        </div>
        <div className="px-4 py-3 space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className={`h-10 rounded-lg animate-pulse ${dark ? 'bg-white/4' : 'bg-slate-100'}`}
              style={{ opacity: 1 - i * 0.09 }} />
          ))}
        </div>
      </div>
    )
  }

  // ── Empty state ───────────────────────────────────────────────────────────
  if (!grupos.length) {
    return (
      <div className={`rounded-xl border ${cardBg}`}>
        <EmptyState
          dark={dark}
          title={t('production.noResultsTitle')}
          description={t('production.noResultsSub')}
        />
      </div>
    )
  }

  // ── Table ─────────────────────────────────────────────────────────────────
  return (
    <div className={`rounded-xl border overflow-hidden shadow-sm ${cardBg}`}>

      {/* Toolbar */}
      <TableToolbar
        dark={dark}
        left={
          <span className={`text-[11px] font-semibold ${mutedTxt}`}>
            {grupos.length.toLocaleString('es-ES')} {t('production.col.orders')}
            {sorted.length !== grupos.length && (
              <span className={`ml-2 font-normal`}>· {sorted.length.toLocaleString('es-ES')} {t('common.records')}</span>
            )}
          </span>
        }
        right={
          <span className="flex items-center gap-2">
            <AvisoExport error={errorExport} dark={dark} />
            <button onClick={exportExcel} disabled={exporting} className={`${btnNeutral} ${exporting ? 'opacity-50 cursor-not-allowed' : ''}`} title={t('common.exportExcel')}>
              <svg className="w-3.5 h-3.5 inline mr-1" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12h10M8 3v7M5 7l3 3 3-3"/>
              </svg>
              {t('common.exportExcel')}
            </button>
          </span>
        }
      />

      <BarraMarcas dark={dark} marcas={marcas} />

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead className={dark ? 'bg-slate-800/50' : 'bg-slate-50/80'}>
            <tr>
              {visibleCols.map(c => {
                if (c.key === 'marca') return <CabeceraMarca key="marca" />
                const label = c.labelKey ? t(c.labelKey) : ''
                const menu = AGG_KEYS.has(c.key) ? (
                  <AggregationMenu
                    dark={dark}
                    colLabel={label}
                    active={agg.aggState[c.key] ?? null}
                    onChange={fn => agg.setAgg(c.key, fn)}
                  />
                ) : null

                if (c.sortKey) {
                  return (
                    <SortableHeader
                      key={c.key}
                      dark={dark}
                      label={label}
                      sortKey={c.sortKey}
                      activeSortKey={sortKey}
                      activeSortDir={sortDir}
                      onClick={() => handleSort(c.sortKey as ProduccionDiariaSortKey)}
                      align={c.align === 'right' ? 'right' : 'left'}
                      extra={menu}
                    />
                  )
                }
                return (
                  <th
                    key={c.key}
                    className={`group ${thBase} cursor-default ${c.key === 'expand' ? 'w-8' : ''} ${c.align === 'right' ? 'text-right' : ''}`}
                  >
                    <span className={`inline-flex items-center gap-1 ${c.align === 'right' ? 'flex-row-reverse' : ''}`}>
                      {label}
                      {menu}
                    </span>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {pagedGrupos.map(g => {
              const isOpen = expanded.has(g.numOp)
              const clave  = claveFila(g.numOp)
              const rend   = rendPct(g.qtyProducidaTotal, g.qtyPlanificada)
              const rowBg  = dark ? 'hover:bg-white/3' : 'hover:bg-slate-50/60'
              const hasMultipleLots = g.distinctLotCount > 1

              return (
                <Fragment key={g.numOp}>
                  {/* ── Summary row ── */}
                  <FilaMarcable
                    marcas={marcas}
                    clave={clave}
                    dark={dark}
                    className={`${rowBg} border-t ${dark ? 'border-white/4' : 'border-slate-100'} transition-colors`}
                  >
                    <MarcaCelda marcas={marcas} clave={clave} dark={dark} />

                    {/* Expand button */}
                    <td className={`${tdBase} pl-4 pr-1 w-8`}>
                      <button
                        onClick={() => toggleExpand(g.numOp)}
                        className={`w-5 h-5 rounded flex items-center justify-center transition-colors cursor-pointer ${
                          dark ? 'text-slate-500 hover:text-slate-200 hover:bg-white/8' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100'
                        }`}
                        title={isOpen ? t('production.consumo.closeConsumptions') : t('production.consumo.viewConsumptions')}
                      >
                        <svg className="w-3 h-3" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                          {isOpen ? <path d="M2 4l3 3 3-3" /> : <path d="M4 2l3 3-3 3" />}
                        </svg>
                      </button>
                    </td>

                    {/* Docs */}
                    <td className={`${tdBase} pr-1`}>
                      <button
                        onClick={() => setAdjuntosNumOp(g.numOp)}
                        title={t('production.btn.viewDocs')}
                        className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg border transition-colors cursor-pointer font-medium ${
                          dark
                            ? 'border-white/10 bg-white/5 text-slate-400 hover:bg-sky-500/15 hover:text-sky-400 hover:border-sky-500/30'
                            : 'border-slate-200 bg-white text-slate-500 hover:bg-sky-50 hover:text-sky-600 hover:border-sky-200 shadow-sm'
                        }`}
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 2H4a1 1 0 00-1 1v10a1 1 0 001 1h8a1 1 0 001-1V6L9 2z"/>
                          <path d="M9 2v4h4M6 9h4M6 11.5h2.5"/>
                        </svg>
                        <span>{t('production.btn.docs')}</span>
                      </button>
                    </td>

                    {/* NumOp */}
                    <td className={`${tdBase} font-mono text-[11px] font-semibold`}>{g.numOp}</td>

                    {/* Articulo */}
                    <td className={`${tdBase} font-semibold`}>{fmtArt(g.articulo)}</td>

                    {/* Descripcion */}
                    <td className={`${tdBase} max-w-[200px] truncate`} title={g.descripcion}>{g.descripcion}</td>

                    {/* Lotes badge */}
                    <td className={tdBase}>
                      {hasMultipleLots ? (
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold ${
                          dark ? 'bg-sky-500/15 text-sky-400' : 'bg-sky-50 text-sky-600 border border-sky-200'
                        }`}>
                          {g.distinctLotCount} {t('production.col.lotsCount')}
                        </span>
                      ) : (
                        <span className={`text-[11px] font-mono ${mutedTxt}`}>{g.lotes[0]?.lote ?? '—'}</span>
                      )}
                    </td>

                    {/* Estado */}
                    <td className={tdBase}>
                      <StatusBadge status={g.estadoOp} dark={dark} namespace="production" />
                    </td>

                    {/* Almacén (optional) */}
                    {isEmpresaAll && <td className={tdBase}>{g.almacenDestino ?? '—'}</td>}

                    {/* Fecha última producción */}
                    <td className={tdBase}>{fmtDate(g.fechaUltima)}</td>

                    {/* QTY OT (planificada de la OP) */}
                    <td className={`${tdBase} text-right tabular-nums ${mutedTxt}`}>{fmtQty(g.qtyPlanificada)}</td>

                    {/* Qty Producida (sum) */}
                    <td className={`${tdBase} text-right tabular-nums font-semibold`}>{fmtQty(g.qtyProducidaTotal)}</td>

                    {/* Rendimiento — bandas de adherencia centradas en el 100 % (ver utils/adherencia) */}
                    <td className={`${tdBase} text-right`}>
                      {rend !== null ? (
                        <div className="flex flex-col items-end gap-0.5">
                          <span className={`text-[11px] font-bold tabular-nums leading-none ${adherenceTextCls(rend, dark)}`}>
                            {rend}%
                          </span>
                          <div className={`w-12 h-1 rounded-full overflow-hidden ${dark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                            <div className={`h-full rounded-full ${adherenceBarCls(rend, dark)}`}
                              style={{ width: `${Math.min(rend, 100)}%` }} />
                          </div>
                        </div>
                      ) : <span className={mutedTxt}>—</span>}
                    </td>

                    {/* Motivo de desviación — nota de solo lectura, solo rol Empresa.All */}
                    {isEmpresaAll && (
                      <td className={`${tdBase} max-w-[220px] truncate`}>
                        {g.comentarios
                          ? <CommentHoverCard text={g.comentarios} dark={dark} />
                          : <span className={mutedTxt}>—</span>}
                      </td>
                    )}
                  </FilaMarcable>

                  {/* ── Expanded: lotes subtable + consumos ── */}
                  {isOpen && (
                    <>
                      {/* Lotes */}
                      <tr className={dark ? 'border-t border-white/4' : 'border-t border-slate-100'}>
                        <LotesSubtable dark={dark} lotes={g.lotes} distinctLotCount={g.distinctLotCount} isEmpresaAll={isEmpresaAll} colSpan={colCount} marcas={marcas} clavePadre={clave} />
                      </tr>
                      {/* Consumos de componentes */}
                      <tr className={dark ? 'bg-slate-800/40 border-t border-white/4' : 'bg-slate-50/80 border-t border-slate-100'}>
                        <ConsumosSubRow dark={dark} numOp={g.numOp} colSpan={colCount} marcas={marcas} clavePadre={clave} />
                      </tr>
                    </>
                  )}
                </Fragment>
              )
            })}
          </tbody>
          {pagedGrupos.length > 0 && (
            <TableTotalsFooter
              dark={dark}
              columns={visibleCols.map(c => ({
                key: c.key,
                decimals: AGG_COLS.find(a => a.key === c.key)?.decimals,
                align: c.align === 'right' ? 'right' : 'left',
              }))}
              results={agg.results}
              rowCount={totalGroups}
            />
          )}
        </table>
      </div>

      {/* Pagination over groups */}
      {totalPages > 1 && (
        <Pagination
          dark={dark}
          page={safePage}
          totalPages={totalPages}
          totalItems={totalGroups}
          itemLabel={t('production.col.orders')}
          pageLabel={t('common.page')}
          ofLabel={t('common.of')}
          onFirst={() => setPage(1)}
          onPrev={() => setPage(safePage - 1)}
          onNext={() => setPage(safePage + 1)}
          onLast={() => setPage(totalPages)}
        />
      )}

      {adjuntosNumOp && (
        <AdjuntosModal
          dark={dark}
          pedidoId={adjuntosNumOp}
          queryKeyPrefix="produccion-adjuntos"
          fetchAdjuntos={produccionDiariaApi.getAdjuntos}
          onClose={() => setAdjuntosNumOp(null)}
        />
      )}
    </div>
  )
}
